import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";

// ─── Datetime helpers (exported for unit tests) ───────────────────────────────

/**
 * Converts an ISO string (UTC) to a datetime-local input value (local time, no tz).
 * Output format: "YYYY-MM-DDTHH:mm"
 */
export function toLocalInput(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  // Adjust for local timezone offset
  const offset = d.getTimezoneOffset() * 60000;
  const local = new Date(d.getTime() - offset);
  return local.toISOString().slice(0, 16);
}

/**
 * Converts a datetime-local string ("YYYY-MM-DDTHH:mm") to a UTC ISO string.
 */
export function toISO(localStr) {
  if (!localStr) return "";
  return new Date(localStr).toISOString();
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
};

const cardStyle = {
  background: "#FFFFFF",
  border: "1px solid #E2E2E2",
  borderRadius: 16,
  padding: 28,
  maxWidth: 560,
  width: "90%",
  boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
  maxHeight: "85vh",
  overflowY: "auto",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #E2E2E2",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 13,
  fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
  color: "#151515",
  boxSizing: "border-box",
  outline: "none",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#3E3E3E",
  marginBottom: 4,
};

const fieldStyle = {
  marginBottom: 14,
};

const errorStyle = {
  padding: "8px 12px",
  background: "#FFF5F5",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  color: "#e53935",
  fontSize: 13,
  marginBottom: 14,
};

// ─── Component ────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  title: "",
  body: "",
  startsAt: "",
  endsAt: "",
  isActive: true,
  textAlign: "left",
  ctaLabel: "",
  ctaUrl: "",
};

export function AnnouncementManagementModal({ onClose }) {
  const { user } = useAuth();
  const [view, setView] = useState("list"); // 'list' | 'create' | 'edit'
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // ─── Fetch list ────────────────────────────────────────────────────────────
  const fetchRows = useCallback(() => {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    supabase
      .from("announcements")
      .select("*")
      .order("starts_at", { ascending: false })
      .then(({ data, error: fetchError }) => {
        setLoading(false);
        if (fetchError) { setError(fetchError.message); return; }
        setRows(data ?? []);
      });
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  if (!supabase) return null;

  // ─── Form helpers ─────────────────────────────────────────────────────────
  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setView("create");
  }

  function openEdit(row) {
    setEditing(row);
    setForm({
      title: row.title ?? "",
      body: row.body ?? "",
      startsAt: toLocalInput(row.starts_at),
      endsAt: toLocalInput(row.ends_at),
      isActive: row.is_active,
      textAlign: row.text_align ?? "left",
      ctaLabel: row.cta_label ?? "",
      ctaUrl: row.cta_url ?? "",
    });
    setError(null);
    setView("edit");
  }

  function backToList() {
    setView("list");
    setEditing(null);
    setError(null);
    fetchRows();
  }

  function handleField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  // ─── Validation ───────────────────────────────────────────────────────────
  function validate() {
    if (!form.title.trim()) return "Title is required.";
    if (!form.startsAt || !form.endsAt) return "Starts at and ends at are required.";
    if (new Date(toISO(form.endsAt)) <= new Date(toISO(form.startsAt))) {
      return "Ends at must be after starts at.";
    }
    const hasLabel = form.ctaLabel.trim() !== "";
    const hasUrl = form.ctaUrl.trim() !== "";
    if (hasLabel !== hasUrl) return "CTA label and URL must both be set or both be empty.";
    if (hasUrl && !form.ctaUrl.trim().match(/^https?:\/\//)) {
      return "CTA URL must start with http:// or https://.";
    }
    return null;
  }

  // ─── Save (create / edit) ─────────────────────────────────────────────────
  async function handleSave() {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    setError(null);

    const payload = {
      title: form.title.trim(),
      body: form.body.trim() || null,
      starts_at: toISO(form.startsAt),
      ends_at: toISO(form.endsAt),
      is_active: form.isActive,
      text_align: form.textAlign,
      cta_label: form.ctaLabel.trim() || null,
      cta_url: form.ctaUrl.trim() || null,
    };

    let result;
    if (view === "create") {
      payload.created_by = user?.id ?? null;
      result = await supabase.from("announcements").insert(payload);
    } else {
      result = await supabase
        .from("announcements")
        .update(payload)
        .eq("id", editing.id);
    }

    setSaving(false);
    if (result.error) { setError(result.error.message); return; }
    backToList();
  }

  // ─── Delete ───────────────────────────────────────────────────────────────
  async function handleDelete(id) {
    const { error: deleteError } = await supabase
      .from("announcements")
      .delete()
      .eq("id", id);
    if (deleteError) { setError(deleteError.message); return; }
    setConfirmDeleteId(null);
    fetchRows();
  }

  // ─── Toggle is_active ────────────────────────────────────────────────────
  async function handleToggle(row) {
    await supabase
      .from("announcements")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    fetchRows();
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  const modal = (
    <div
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h4
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: "#151515",
              margin: 0,
              fontFamily: "'Clash Display Variable','Clash Display',sans-serif",
            }}
          >
            {view === "list" ? "Manage Announcements" : view === "create" ? "New Announcement" : "Edit Announcement"}
          </h4>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "#626262", fontSize: 20, cursor: "pointer" }}
          >
            ✕
          </button>
        </div>

        {/* Error banner */}
        {error && <div style={errorStyle}>{error}</div>}

        {/* ── List view ─────────────────────────────────────────────────────── */}
        {view === "list" && (
          <>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
              <button
                onClick={openCreate}
                style={{
                  padding: "8px 16px",
                  background: "#F90091",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                + New
              </button>
            </div>

            {loading && (
              <div style={{ textAlign: "center", padding: 24, color: "#7b7b7b", fontSize: 13 }}>Loading…</div>
            )}

            {!loading && rows.length === 0 && (
              <div style={{ textAlign: "center", padding: 24, color: "#7b7b7b", fontSize: 13 }}>
                No announcements yet. Click &quot;+ New&quot; to create one.
              </div>
            )}

            {!loading && rows.map((row) => (
              <div
                key={row.id}
                style={{
                  border: "1px solid #E2E2E2",
                  borderRadius: 10,
                  padding: "14px 16px",
                  marginBottom: 10,
                  background: "#FAFAFA",
                }}
              >
                {/* Confirm delete prompt */}
                {confirmDeleteId === row.id ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 13, color: "#151515", flex: 1 }}>
                      Are you sure you want to delete &quot;{row.title}&quot;?
                    </span>
                    <button
                      onClick={() => handleDelete(row.id)}
                      style={{ padding: "6px 12px", background: "#DC2626", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      style={{ padding: "6px 12px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#151515" }}>{row.title}</div>
                        <div style={{ fontSize: 12, color: "#7b7b7b", marginTop: 2 }}>
                          {toLocalInput(row.starts_at)} → {toLocalInput(row.ends_at)}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 20,
                              background: row.is_active ? "#DCFCE7" : "#F3F4F6",
                              color: row.is_active ? "#166534" : "#6B7280",
                            }}
                          >
                            {row.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                        <button
                          onClick={() => handleToggle(row)}
                          style={{ padding: "5px 10px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
                        >
                          {row.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          onClick={() => openEdit(row)}
                          style={{ padding: "5px 10px", background: "transparent", color: "#F90091", border: "1px solid #F9009133", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(row.id)}
                          style={{ padding: "5px 10px", background: "transparent", color: "#DC2626", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        )}

        {/* ── Create / Edit form ─────────────────────────────────────────────── */}
        {(view === "create" || view === "edit") && (
          <>
            <div style={fieldStyle}>
              <label htmlFor="ann-title" style={labelStyle}>Title *</label>
              <input
                id="ann-title"
                aria-label="Title"
                type="text"
                value={form.title}
                onChange={(e) => handleField("title", e.target.value)}
                style={inputStyle}
                placeholder="Announcement title"
              />
            </div>

            <div style={fieldStyle}>
              <label htmlFor="ann-body" style={labelStyle}>Body (optional)</label>
              <textarea
                id="ann-body"
                aria-label="Body"
                value={form.body}
                onChange={(e) => handleField("body", e.target.value)}
                style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
                placeholder="Optional description or detail"
              />
            </div>

            <div style={fieldStyle}>
              <span style={labelStyle}>Text alignment</span>
              <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                {["left", "center", "right"].map((align) => (
                  <button
                    key={align}
                    type="button"
                    aria-label={`Align ${align}`}
                    onClick={() => handleField("textAlign", align)}
                    style={{
                      flex: 1,
                      padding: "6px 0",
                      borderRadius: 6,
                      border: form.textAlign === align ? "1.5px solid #F90091" : "1px solid #E2E2E2",
                      background: form.textAlign === align ? "#FFF5FB" : "transparent",
                      color: form.textAlign === align ? "#F90091" : "#626262",
                      fontSize: 13,
                      fontWeight: form.textAlign === align ? 600 : 400,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {align}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="ann-starts-at" style={labelStyle}>Starts at *</label>
                <input
                  id="ann-starts-at"
                  aria-label="Starts at"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => handleField("startsAt", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="ann-ends-at" style={labelStyle}>Ends at *</label>
                <input
                  id="ann-ends-at"
                  aria-label="Ends at"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => handleField("endsAt", e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ ...fieldStyle, display: "flex", alignItems: "center", gap: 8 }}>
              <input
                id="ann-is-active"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => handleField("isActive", e.target.checked)}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
              <label htmlFor="ann-is-active" style={{ ...labelStyle, marginBottom: 0 }}>Active</label>
            </div>

            <div style={{ borderTop: "1px solid #F0F0F0", paddingTop: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: "#7b7b7b", marginBottom: 10 }}>
                CTA (optional — both fields required together)
              </div>
              <div style={fieldStyle}>
                <label htmlFor="ann-cta-label" style={labelStyle}>CTA Label</label>
                <input
                  id="ann-cta-label"
                  aria-label="CTA Label"
                  type="text"
                  value={form.ctaLabel}
                  onChange={(e) => handleField("ctaLabel", e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Learn more"
                />
              </div>
              <div style={fieldStyle}>
                <label htmlFor="ann-cta-url" style={labelStyle}>CTA URL</label>
                <input
                  id="ann-cta-url"
                  aria-label="CTA URL"
                  type="text"
                  value={form.ctaUrl}
                  onChange={(e) => handleField("ctaUrl", e.target.value)}
                  style={inputStyle}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={backToList}
                style={{ padding: "8px 18px", background: "transparent", color: "#626262", border: "1px solid #E2E2E2", borderRadius: 8, fontSize: 13, cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ padding: "8px 18px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
